import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../../ts/Glob';
import * as Globalization from '../../../../_common/platform/ts/Globalization';
import * as reactGlobalClick from '../../../../_common/platform/ts/reactGlobalClick';
import { IComponentRef, IEdgeFlags, IBaseWidgetParameters, ILayoutData, IWidget } from '../../ts/Interfaces';
import { finalizeMenuHandler, getOnConfigClickHandler, getOnShowWidgetHelpHandler } from './WidgetMenu';
import { IValuesCollection } from '../../../../_common/platform/ts/appTheme';
import { IMenuItemFlags } from '../../../../_common/platform/ts/WidgetBase';
import { IActionType } from '../../../../_common/platform/ts/printContentUtils';
import { themeVars } from '../../ts/themes/themeConstants';

function DockingToggler(props: { edgeFlags: IEdgeFlags; docked: boolean }) {
    const { top, bottom, left, right } = props.edgeFlags;

    let iconClass = 'bi-';
    let docktitle = '';

    if (props.docked) {
        if (left && !right) {
            iconClass += 'caret-right expand-right';
        } else if (right && !left) {
            iconClass += 'caret-left expand-left';
        } else if (bottom && !top) {
            iconClass += 'caret-up expand-up';
        } else if (top && !bottom) {
            iconClass += 'caret-down expand-down';
        }

        docktitle = 'Click header to undock';
    } else {
        if (left && !right) {
            iconClass += 'caret-left';
        } else if (right && !left) {
            iconClass += 'caret-right';
        } else if (bottom && !top) {
            iconClass += 'caret-down';
        } else if (top && !bottom) {
            iconClass += 'caret-up';
        }

        docktitle = 'Click header to dock';
    }

    return <div className={iconClass + ' dock-toggle-button header-button d-print-none'} title={docktitle} />;
}

function allowEditLayout(constr) {
    return constr != null ? constr.prototype.allowEditLayout() : true;
}

function WidgetHeader(
    props: {
        workspace: number;
        tab: number;
        data: IWidget;
        widgetValues: IBaseWidgetParameters;
        editMode: boolean;
        menuFlags: IMenuItemFlags;
        layoutType: string;
        layoutData: ILayoutData;
        layoutEdit: boolean;
        edgeFlags: IEdgeFlags;
        constr;
        hasMenu: boolean;
        hideMenuButton: boolean;
        locale: string;
        theme: IValuesCollection;
        glob: IGlob;
        componentRefGetter: () => IComponentRef;
        getOnPrintHandler(actionType: IActionType, theme: IValuesCollection): React.MouseEventHandler<any>;
        onClick(e): void;
        onExportToExcelClick(e): void;
        onToggleMenu(e): void;
        onToggleDocking(): void;
        onLayoutModeChange(layoutEdit: boolean): void;
    },
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const { widgetValues, theme, menuFlags, locale } = props;
    const { docked, dockedVertically } = props.layoutData ?? {};
    const { tooltip } = widgetValues;
    const togglingIsOn = props.onToggleDocking != null;
    const headerElements: React.JSX.Element[] = [];

    if (props.layoutType !== 'narrow') {
        if (props.editMode) {
            headerElements.push(<RemoveButton key={'he_remove-button'} id={props.data.id} glob={props.glob} />);

            headerElements.push(
                <span
                    key={'he_config-button'}
                    className={'header-button config-button glyphicon glyphicon-cog d-print-none'}
                    onClick={getOnConfigClickHandler(
                        props.workspace,
                        props.tab,
                        props.data,
                        theme,
                        props.glob,
                        props.componentRefGetter
                    )}
                />
            );

            if (allowEditLayout(props.constr)) {
                headerElements.push(
                    <EditWidgetLayoutButton
                        key="he_editing-button"
                        layoutEdit={props.layoutEdit}
                        onLayoutModeChange={props.onLayoutModeChange}
                    />
                );
            }
        } else {
            if (props.hasMenu) {
                const description = widgetValues.description || null;

                if (!props.hideMenuButton) {
                    headerElements.push(
                        <span
                            key={'he_menu-button'}
                            className={'glyphicon glyphicon-option-horizontal menu-button header-button d-print-none'}
                            onClick={props.onToggleMenu}
                        />
                    );
                }

                if (description != null && description !== '') {
                    headerElements.push(
                        <span
                            key={'he_help-button'}
                            className={'glyphicon glyphicon-question-sign menu-button header-button d-print-none'}
                            title={tooltip}
                            onClick={getOnShowWidgetHelpHandler(description, tooltip, props.glob)}
                        />
                    );
                }

                if (menuFlags.exportToExcel) {
                    if (theme[themeVars.widgetShowExportToExcelButton] === true) {
                        headerElements.push(
                            <span
                                key={'he_excel-button'}
                                className={'fa fa-file-excel-o menu-button header-button d-print-none'}
                                title={Globalization.getMessage('exportToExcel', locale)}
                                onClick={props.onExportToExcelClick}
                            />
                        );
                    }
                }

                if (menuFlags.print) {
                    if (theme[themeVars.widgetShowPdfButton] === true) {
                        headerElements.push(
                            <span
                                key={'he_generate-pdf-button'}
                                className={'fa fa-file-pdf-o menu-button header-button d-print-none'}
                                title={Globalization.getMessage('generatePDF', locale)}
                                onClick={props.getOnPrintHandler(IActionType.exportToPdf, theme)}
                            />
                        );
                    }

                    if (theme[themeVars.widgetShowPrintButton] === true) {
                        headerElements.push(
                            <span
                                key={'he_print-button'}
                                className={'fa fa-print menu-button header-button d-print-none'}
                                title={Globalization.getMessage('print', locale)}
                                onClick={props.getOnPrintHandler(IActionType.print, theme)}
                            />
                        );
                    }
                }
            }
        }
    }

    //IL: See Issue 87087.
    const style: React.CSSProperties = {
        cursor: togglingIsOn ? 'pointer' : 'default'
    };

    if (props.editMode) {
        style.color = '#FFFFFF';
        style.background = '#8F8F8F';
    }

    let widgetTitle = '\u00A0'; //VR: some widget (ex: DrillDown with default name) has not attr 'Name' !!

    // So, an Error occurs on ".replace..."  - be care full here, Irina.
    if (widgetValues.name != null) {
        widgetTitle = widgetValues.name.replace(new RegExp(' ', 'g'), '\u00A0'); //replace added for white spaces
    }

    let toggler: React.JSX.Element;

    if (togglingIsOn) {
        toggler = <DockingToggler edgeFlags={props.edgeFlags} docked={docked} />;
    }

    return (
        <div
            className={'widget-control-panel' + (docked ? ' docked' : '') + (dockedVertically ? ' rotate' : '')}
            ref={ref}
            style={style}
            onClick={props.onClick}
        >
            <span className={'widget-title'} title={tooltip}>
                {widgetTitle}
            </span>

            {headerElements}
            {toggler}
        </div>
    );
}

function RemoveButton(props: { id: number; glob: IGlob }) {
    const handleOnClick = React.useCallback(
        (e) => {
            props.glob.AppActions.widgetRemove(props.id);
            finalizeMenuHandler(e);
        },
        [props.id]
    );

    return (
        <span
            className={'header-button remove-button glyphicon glyphicon-remove d-print-none'}
            onClick={handleOnClick}
        />
    );
}

function EditWidgetLayoutButton(props: { layoutEdit: boolean; onLayoutModeChange(layoutEdit: boolean): void }) {
    const handleOnClick = React.useCallback(
        (e) => {
            if (props.onLayoutModeChange != null) {
                props.onLayoutModeChange(!props.layoutEdit);
                reactGlobalClick.cancel(1);
            }
        },
        [props.onLayoutModeChange, props.layoutEdit]
    );

    return (
        <div
            className="header-button editing-button fa fa-cogs d-print-none"
            title={'Edit detailed widget presentation'}
            onClick={handleOnClick}
        >
            <span> {props.layoutEdit ? 'Stop Editing' : null} </span>
        </div>
    );
}

export default React.forwardRef(WidgetHeader);
